import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { Accordion, useAccordionContext } from "@core/components/Accordion";
import { Icon } from "@core/components/Icon";
import { useExpandableContent } from "@core/hooks/useExpandableContent";
import classNames from "classnames";

import { UiMinusIcon } from "@jti/brand-icons/ui-minus";
import { UiPlusIcon } from "@jti/brand-icons/ui-plus";

import styles from "./AccordionItem.module.scss";

export type AccordionItemProps = {
  id: string;
  title: string;
  disabled?: boolean;
  onToggle?: (isExpanded: boolean) => void;
  children?: ReactNode;
};

const normalizeAccordionItemId = (value: string) => value.trim().replace(/\s+/g, "-");

export const AccordionItem = ({ id, title, disabled = false, onToggle, children }: AccordionItemProps) => {
  const accordionContext = useAccordionContext();
  const isExpanded = useMemo(() => !!accordionContext?.expandedIds.includes(id), [id, accordionContext]);
  const wasExpanded = useRef(isExpanded);
  const safeId = normalizeAccordionItemId(id);

  const { innerRef, containerHeight, onTransitionEnd, updateHeight } = useExpandableContent({ isExpanded });

  useEffect(() => {
    if (isExpanded === wasExpanded.current) return;
    wasExpanded.current = isExpanded;
    onToggle?.(isExpanded);
  }, [isExpanded, onToggle]);

  const toggle = useCallback(() => {
    if (disabled) return;

    if (isExpanded) {
      updateHeight();
    }
    accordionContext?.toggleAccordionItem({ id, title });
  }, [disabled, isExpanded, accordionContext, id, title, updateHeight]);

  if (!accordionContext) {
    return (
      <Accordion>
        <AccordionItem id={id} title={title} disabled={disabled} onToggle={onToggle}>
          {children}
        </AccordionItem>
      </Accordion>
    );
  }

  const { headingLevel: Heading, useRegionRole } = accordionContext;
  const headingId = `accordion-heading-${safeId}`;
  const panelId = `accordion-panel-${safeId}`;
  const IconIndicator = isExpanded ? UiMinusIcon : UiPlusIcon;

  return (
    <div
      className={classNames(styles.container, {
        [styles.expanded]: isExpanded,
        [styles.disabled]: disabled,
      })}
    >
      <Heading className={styles.heading}>
        <button
          type="button"
          id={headingId}
          className={styles.trigger}
          onClick={toggle}
          data-testid={`accordion-title-${id}`}
          aria-expanded={isExpanded}
          aria-controls={panelId}
          disabled={disabled}
        >
          <span className={styles.title}>{title}</span>
          <Icon icon={IconIndicator} aria-hidden="true" size="sm" />
        </button>
      </Heading>
      <div
        id={panelId}
        role={useRegionRole ? "region" : undefined}
        aria-labelledby={useRegionRole ? headingId : undefined}
        className={styles.content}
        style={{ maxHeight: containerHeight }}
        onTransitionEnd={onTransitionEnd}
      >
        <div className={styles.innerContent} ref={innerRef} data-testid={`accordion-item-${id}`}>
          {children}
        </div>
      </div>
    </div>
  );
};
